import { Component, ElementRef, HostListener, computed, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterLink } from '@angular/router';
import { AuthService, Role } from '../auth/auth.service';

interface CreateItem { label: string; path: string; minRole?: Role; }
interface CreateColumn { title: string; items: CreateItem[]; }

/** "+ New" in the top bar: every create form in one place, grouped by area. */
const COLUMNS: CreateColumn[] = [
  {
    title: 'Customers',
    items: [
      { label: 'Invoice',          path: '/invoices/new' },
      { label: 'Receive payment',  path: '/payments/new/receipt' },
      { label: 'Credit note',      path: '/credits/customer/new' },
      { label: 'Refund',           path: '/refunds/new' },
      { label: 'Customer',         path: '/customers/new' }
    ]
  },
  {
    title: 'Suppliers',
    items: [
      { label: 'Bill',             path: '/bills/new' },
      { label: 'Expense',          path: '/expenses/new' },
      { label: 'Pay bills',        path: '/payments/new/payment' },
      { label: 'Supplier credit',  path: '/credits/vendor/new' },
      { label: 'Refund receipt',   path: '/refund-receipts/new' },
      { label: 'Supplier',         path: '/vendors/new' }
    ]
  },
  {
    title: 'Other',
    items: [
      { label: 'Journal entry',      path: '/journal/new' },
      { label: 'Product or service', path: '/products/new' },
      { label: 'Account',            path: '/accounts/new', minRole: 'Admin' }
    ]
  }
];

@Component({
  selector: 'app-create-menu',
  standalone: true,
  imports: [CommonModule, RouterLink],
  template: `
    @if (canCreate()) {
      <div class="create">
        <button type="button" class="trigger" [class.open]="open()" (click)="toggle($event)" aria-haspopup="true" [attr.aria-expanded]="open()">
          <span class="plus" aria-hidden="true">+</span> New
        </button>
        @if (open()) {
          <div class="menu" role="menu" [style.top.px]="menuTop()" [style.left.px]="menuLeft()">
            @for (col of columns(); track col.title) {
              <div class="col">
                <div class="title">{{ col.title }}</div>
                @for (item of col.items; track item.path) {
                  <a [routerLink]="item.path" (click)="open.set(false)" role="menuitem">{{ item.label }}</a>
                }
              </div>
            }
          </div>
        }
      </div>
    }
  `,
  styles: [`
    :host { display: block; }
    .trigger {
      display: flex; align-items: center; justify-content: center; gap: 6px; width: 100%;
      padding: 9px 14px; border: 0; border-radius: var(--radius-full);
      background: var(--color-brand); color: var(--color-text-invert);
      font: inherit; font-size: var(--fs-sm); font-weight: 600; cursor: pointer;
      transition: background 0.12s;
    }
    .trigger:hover, .trigger.open { background: var(--color-brand-hover); }
    .plus { font-size: 16px; line-height: 1; transition: transform 0.15s; }
    .trigger.open .plus { transform: rotate(45deg); }

    /* Fixed, so the scrolling sidebar doesn't clip it; placed under the button (see toggle). */
    .menu {
      position: fixed; z-index: 60;
      display: grid; grid-template-columns: repeat(3, minmax(150px, auto)); gap: 4px 24px;
      padding: 14px 18px;
      background: var(--color-surface); color: var(--color-text);
      border: 1px solid var(--color-border); border-radius: var(--radius-md); box-shadow: var(--shadow-lg);
      animation: slideDown 0.12s ease-out;
    }
    @keyframes slideDown { from { opacity: 0; transform: translateY(-4px); } to { opacity: 1; transform: translateY(0); } }
    .col { display: flex; flex-direction: column; }
    .title {
      padding: 4px 8px 6px; font-size: 10px; text-transform: uppercase; letter-spacing: 0.08em;
      color: var(--color-text-subtle); font-weight: 600;
    }
    a {
      padding: 6px 8px; border-radius: var(--radius-sm);
      color: var(--color-text); text-decoration: none; font-size: var(--fs-sm); white-space: nowrap;
    }
    a:hover { background: var(--color-brand-softer); color: var(--color-brand-hover); text-decoration: none; }
    @media (max-width: 720px) {
      .menu { grid-template-columns: 1fr; max-height: 70vh; overflow-y: auto; }
    }
  `]
})
export class CreateMenuComponent {
  private auth = inject(AuthService);
  private host = inject(ElementRef<HTMLElement>);

  open = signal(false);
  menuTop = signal(0);
  menuLeft = signal(0);
  canCreate = computed(() => this.auth.hasRole('Editor'));

  columns = computed<CreateColumn[]>(() =>
    COLUMNS.map(c => ({ ...c, items: c.items.filter(i => !i.minRole || this.auth.hasRole(i.minRole)) })));

  toggle(event: Event): void {
    event.stopPropagation();
    const r = (event.currentTarget as HTMLElement).getBoundingClientRect();
    this.menuTop.set(r.bottom + 6);
    this.menuLeft.set(r.left);
    this.open.set(!this.open());
  }

  @HostListener('document:click', ['$event'])
  onDocClick(event: Event): void {
    if (!this.host.nativeElement.contains(event.target as Node)) this.open.set(false);
  }

  @HostListener('window:resize')
  onResize(): void { this.open.set(false); }

  @HostListener('document:keydown.escape')
  onEscape(): void { this.open.set(false); }
}
